import { Text } from '@change/ds-core-ui/Text';
import { CelebrationIcon } from '@change/ds-icons/CelebrationIcon';
import { ReminderIcon } from '@change/ds-icons/ReminderIcon';
import { Grid } from '@change/ds-layout/Grid';

import { Translate } from '@change-corgi/core-react/i18n';

import { DecisionMakerContainer, StepActions } from '#src/app/pages/startAPetitionV2/shared/components';
import { Html } from '#src/app/shared/components/html';
import { useRichMediaTransformers } from '#src/app/shared/hooks/richMedia';

import { BannerActions } from './components/BannerActions';
import { Hero } from './components/Hero';
import { usePreview } from './hooks/usePreview';

export const Preview: React.FC = () => {
	const { data, actions } = usePreview();
	const { decodeRichMediaTransformer } = useRichMediaTransformers();

	return (
		// NOTE: 88px is the height of the StepActions component and 61px is the height of the header
		// min-h-[calc(100vh-88px-61px)] ensures that the content area takes up at least the full height of the viewport minus the header and step actions
		// grid-rows-[min-content_auto_min-content] ensures that the banner actions are at the top and bottom, with the content area taking up the remaining space
		// mb-22 ensures that there is enough space at the bottom for the StepActions component when it is fixed to the bottom of the viewport
		<div className="bg-background-overlay mb-22 grid min-h-[calc(100vh-88px-61px)] grid-rows-[min-content_auto_min-content]">
			<BannerActions
				title={<Translate value="corgi.start_a_petition.preview.banner_actions.top.heading_variant" />}
				description={<Translate value="corgi.start_a_petition.preview.banner_actions.top.description_variant" />}
				icon={<CelebrationIcon />}
			/>
			<Grid className="pt-4 md:pt-8">
				{/* clips only from md up, so the decision-maker container can run edge to edge on mobile */}
				<div className="bg-background-base col-span-full rounded-tl-lg rounded-tr-lg md:overflow-hidden">
					{/* Heading + Image */}
					<div className="overflow-hidden rounded-tl-lg rounded-tr-lg">
						<Hero status={data.jobStatus} imageSrc={data.imageSrc}>
							{data.ask}
						</Hero>
					</div>

					{/* Description */}
					{/* the min height moves from the description (as in Preview) to this wrapper, so the content area stays
					as tall as Preview's whether or not decision makers show; 50vh plus the vertical padding. On mobile the
					section's row takes any extra height, so its gradient still meets the banner */}
					<Grid className="min-h-[calc(50vh+4rem)] grid-rows-[auto_1fr] gap-4 px-0 py-8 md:min-h-[calc(50vh+7rem)] md:grid-rows-none md:content-start md:gap-8 md:py-14">
						<div className="col-span-full space-y-4 px-4 md:col-span-6 md:col-start-2 md:px-0 lg:col-span-8 lg:col-start-3">
							<Text as="h2" variant="heading">
								<Translate value="corgi.start_a_petition.preview.description_heading" />
							</Text>
							<Html html={data.description} className="font-serif" transformers={[decodeRichMediaTransformer]} />
						</div>

						{/* Decision makers, read-only */}
						{/* edge to edge on mobile and flush with the banner below (cancels the grid's bottom padding); mt-2 on top of
						the grid gap leaves 24px under the description on mobile and 40px from md up, as on edit */}
						<DecisionMakerContainer className="col-span-full -mx-4 mt-2 -mb-8 md:col-span-6 md:col-start-2 md:mx-0 md:mb-0 lg:col-span-8 lg:col-start-3" />
					</Grid>
				</div>
			</Grid>
			<BannerActions
				title={<Translate value="corgi.start_a_petition.preview.banner_actions.bottom.heading" />}
				description={<Translate value="corgi.start_a_petition.preview.banner_actions.bottom.description" />}
				icon={<ReminderIcon />}
			/>
			<StepActions
				className="md:bg-background-base! px-0! py-4! md:fixed! md:right-0! md:bottom-0! md:left-0! md:border-t! md:shadow-md! [&>div]:container"
				type="navigation"
				onPrimaryAction={actions.submit}
				onSecondaryAction={actions.back}
			/>
		</div>
	);
};
